/**
 * CoachPlaybook - Scouting Component
 * Version: 0.5.145
 * Description: Dedicated opponent scouting with team ratings and player analysis
 */

import React, { useState, useEffect, useRef } from 'react';
import JerseyIcon, { JERSEY_FONTS, TeamPresets, DEFAULT_JERSEY_TEMPLATE, DEFAULT_POSITIONS } from '../common/JerseyIcon';
import { getSetting, setSetting, getAll, putAll, STORES } from '../../utils/db';

const OPPONENTS_KEY = 'opponents';

// Skill categories for player scouting
const SKILL_CATEGORIES = [
  { key: 'shooting', label: 'Shooting', icon: '🎯', color: '#22c55e' },
  { key: 'driving', label: 'Driving', icon: '💨', color: '#3b82f6' },
  { key: 'passing', label: 'Passing', icon: '👁️', color: '#8b5cf6' },
  { key: 'defense', label: 'Defense', icon: '🛡️', color: '#ef4444' },
  { key: 'rebounding', label: 'Rebounding', icon: '🏀', color: '#f59e0b' },
  { key: 'athleticism', label: 'Speed', icon: '⚡', color: '#06b6d4' },
  { key: 'bbiq', label: 'BBIQ', icon: '🧠', color: '#ec4899' }
];

// Team rating categories (for manual entry)
const TEAM_RATING_CATEGORIES = [
  { key: 'offense', label: 'Offense', icon: '⚔️', color: '#22c55e' },
  { key: 'defense', label: 'Defense', icon: '🛡️', color: '#3b82f6' },
  { key: 'speed', label: 'Pace/Speed', icon: '⚡', color: '#f59e0b' },
  { key: 'discipline', label: 'Discipline', icon: '📋', color: '#8b5cf6' },
  { key: 'coaching', label: 'Coaching', icon: '📣', color: '#ec4899' },
  { key: 'depth', label: 'Bench Depth', icon: '🪑', color: '#06b6d4' }
];

const SKILL_LEVELS = [
  { value: '', label: 'Select Level' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'recreational', label: 'Recreational' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'competitive', label: 'Competitive' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'elite', label: 'Elite' },
  { value: 'pro', label: 'Professional' }
];

// Default opponent template
const DEFAULT_OPPONENT = {
  id: '',
  name: '',
  abbreviation: '',
  league: '',
  leagueSkillLevel: '',
  actualSkillLevel: '',
  fillColor: '#dc2626',
  outlineColor: '#991b1b',
  numberColor: '#ffffff',
  numberOutline: '#991b1b',
  textColor: '#ffffff',
  fontFamily: 'Impact, Haettenschweiler, sans-serif',
  logoUrl: null,
  // Team ratings (manual)
  teamRatings: {
    offense: 50,
    defense: 50,
    speed: 50,
    discipline: 50,
    coaching: 50,
    depth: 50
  },
  useManualRatings: true, // false = calculate from players
  players: [],
  notes: ''
};

// Default scouted player
const DEFAULT_SCOUTED_PLAYER = {
  id: '',
  name: 'Player',
  jerseyNumber: '0',
  position: '',
  gender: 'M',
  notes: '',
  isStarter: false,
  threatLevel: 'medium', // low, medium, high, elite
  numberY: DEFAULT_POSITIONS.numberY,
  nameY: DEFAULT_POSITIONS.nameY,
  numberSize: DEFAULT_POSITIONS.numberSize,
  nameSize: DEFAULT_POSITIONS.nameSize,
  skills: {
    shooting: 50,
    driving: 50,
    passing: 50,
    defense: 50,
    rebounding: 50,
    athleticism: 50,
    bbiq: 50
  }
};

// Helper functions
const getAverageSkill = (skills) => {
  const values = Object.values(skills || {});
  if (values.length === 0) return 50;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
};

const getSkillColor = (value) => {
  if (value >= 80) return '#22c55e';
  if (value >= 60) return '#84cc16';
  if (value >= 40) return '#f59e0b';
  if (value >= 20) return '#f97316';
  return '#ef4444';
};

const getThreatColor = (threat) => {
  switch (threat) {
    case 'elite': return { bg: 'bg-red-600', text: 'text-red-100' };
    case 'high': return { bg: 'bg-orange-600', text: 'text-orange-100' };
    case 'medium': return { bg: 'bg-yellow-600', text: 'text-yellow-100' };
    default: return { bg: 'bg-green-600', text: 'text-green-100' };
  }
};

// Stat display component
const StatDisplay = ({ skills, compact = false }) => {
  if (!skills) return null;
  
  const sortedSkills = Object.entries(skills)
    .map(([key, value]) => {
      const category = SKILL_CATEGORIES.find(c => c.key === key);
      return { key, value, ...category };
    })
    .filter(s => s.label)
    .sort((a, b) => b.value - a.value);
  
  const top3Keys = sortedSkills.slice(0, 3).map(s => s.key);
  const bottom3Keys = sortedSkills.slice(-3).map(s => s.key);
  
  if (compact) {
    return (
      <div className="flex flex-wrap gap-1">
        {sortedSkills.slice(0, 3).map(skill => (
          <span key={skill.key} className="px-2 py-0.5 rounded text-xs bg-green-900/50 text-green-300">
            {skill.icon} {skill.value}
          </span>
        ))}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-2 gap-2">
      {SKILL_CATEGORIES.map(skill => {
        const value = skills[skill.key] || 50;
        const isTop3 = top3Keys.includes(skill.key);
        const isBottom3 = bottom3Keys.includes(skill.key) && !isTop3;
        
        let bgColor = 'bg-slate-800';
        let textColor = 'text-slate-300';
        let valueColor = 'text-white';
        
        if (isTop3) {
          bgColor = 'bg-green-900/50';
          textColor = 'text-green-300';
          valueColor = 'text-green-400';
        } else if (isBottom3) {
          bgColor = 'bg-red-900/50';
          textColor = 'text-red-300';
          valueColor = 'text-red-400';
        }
        
        return (
          <div key={skill.key} className={`flex items-center justify-between px-3 py-2 rounded-lg ${bgColor}`}>
            <div className="flex items-center gap-2">
              <span className="text-sm">{skill.icon}</span>
              <span className={`text-xs font-medium ${textColor}`}>{skill.label}</span>
            </div>
            <span className={`text-sm font-bold ${valueColor}`}>{value}</span>
          </div>
        );
      })}
    </div>
  );
};

// Calculate team ratings from players
const calculateTeamRatings = (players) => {
  if (!players || players.length === 0) return null;
  
  const starters = players.filter(p => p.isStarter).slice(0, 5);
  const bench = players.filter(p => !p.isStarter);
  const allPlayers = players;
  
  // Calculate averages
  const avgShooting = Math.round(allPlayers.reduce((sum, p) => sum + (p.skills?.shooting || 50), 0) / allPlayers.length);
  const avgDriving = Math.round(allPlayers.reduce((sum, p) => sum + (p.skills?.driving || 50), 0) / allPlayers.length);
  const avgPassing = Math.round(allPlayers.reduce((sum, p) => sum + (p.skills?.passing || 50), 0) / allPlayers.length);
  const avgDefense = Math.round(allPlayers.reduce((sum, p) => sum + (p.skills?.defense || 50), 0) / allPlayers.length);
  const avgAthletics = Math.round(allPlayers.reduce((sum, p) => sum + (p.skills?.athleticism || 50), 0) / allPlayers.length);
  const avgBBIQ = Math.round(allPlayers.reduce((sum, p) => sum + (p.skills?.bbiq || 50), 0) / allPlayers.length);
  
  return {
    offense: Math.round((avgShooting + avgDriving + avgPassing) / 3),
    defense: avgDefense,
    speed: avgAthletics,
    discipline: avgBBIQ,
    coaching: 50, // Can't calculate this
    depth: Math.min(100, bench.length * 15 + 25), // More bench = more depth
    playerCount: allPlayers.length,
    starterCount: starters.length
  };
};

const Scouting = ({ onNavigate }) => {
  const [opponents, setOpponents] = useState([]);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [editingOpponent, setEditingOpponent] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [view, setView] = useState('list'); // list, detail, editTeam, editPlayer
  const [detailTab, setDetailTab] = useState('overview'); // overview, players, report
  const [editTab, setEditTab] = useState('info'); // info, jersey, ratings
  const [playerTab, setPlayerTab] = useState('info'); // info, skills, jersey
  const [isLoading, setIsLoading] = useState(true);
  
  const logoInputRef = useRef(null);
  
  // Load opponents
  useEffect(() => {
    const loadData = async () => {
      try {
        // v0.5.39: Load from OPPONENTS store (not settings)
        let saved = await getAll(STORES.OPPONENTS);
        
        // Migration: check if old data exists in settings
        if (!saved || saved.length === 0) {
          const legacyData = await getSetting(OPPONENTS_KEY);
          if (legacyData && legacyData.length > 0) {
            // Migrate to new store
            await putAll(STORES.OPPONENTS, legacyData);
            saved = legacyData;
            console.log('Migrated opponents from settings to OPPONENTS store');
          }
        }
        
        if (saved && saved.length > 0) {
          const migrated = saved.map(o => ({
            ...DEFAULT_OPPONENT,
            ...o,
            teamRatings: { ...DEFAULT_OPPONENT.teamRatings, ...(o.teamRatings || {}) },
            useManualRatings: o.useManualRatings !== false,
            players: (o.players || []).map(p => ({
              ...DEFAULT_SCOUTED_PLAYER,
              ...p
            }))
          }));
          setOpponents(migrated);
        }
      } catch (error) {
        console.error('Failed to load opponents:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);
  
  const saveOpponents = async (newOpponents) => {
    setOpponents(newOpponents);
    // v0.5.39: Save to OPPONENTS store (for backup/export support)
    await putAll(STORES.OPPONENTS, newOpponents);
  };
  
  // CRUD Operations
  const handleAddOpponent = () => {
    setEditingOpponent({ ...DEFAULT_OPPONENT, id: `opp_${Date.now()}` });
    setEditTab('info');
    setView('editTeam');
  };
  
  const handleEditOpponent = (opponent) => {
    setEditingOpponent({ ...DEFAULT_OPPONENT, ...opponent });
    setEditTab('info');
    setView('editTeam');
  };
  
  const handleSaveOpponent = async () => {
    if (!editingOpponent?.name.trim()) {
      alert('Team name is required');
      return;
    }
    
    const opponent = {
      ...editingOpponent,
      abbreviation: editingOpponent.abbreviation || editingOpponent.name.slice(0, 3).toUpperCase()
    };
    
    const existing = opponents.find(o => o.id === opponent.id);
    const newOpponents = existing
      ? opponents.map(o => o.id === opponent.id ? opponent : o)
      : [...opponents, opponent];
    
    await saveOpponents(newOpponents);
    setSelectedOpponent(opponent);
    setView('detail');
    setEditingOpponent(null);
  };
  
  const handleDeleteOpponent = async (opponentId) => {
    if (!window.confirm('Delete this opponent and all scouted players?')) return;
    const newOpponents = opponents.filter(o => o.id !== opponentId);
    await saveOpponents(newOpponents);
    setSelectedOpponent(null);
    setView('list');
  };
  
  const handleViewOpponent = (opponent) => {
    setSelectedOpponent(opponent);
    setDetailTab('overview');
    setView('detail');
  };
  
  // Player operations
  const handleAddPlayer = () => {
    const playerCount = (selectedOpponent?.players?.length || 0) + 1;
    setEditingPlayer({
      ...DEFAULT_SCOUTED_PLAYER,
      id: `player_${Date.now()}`,
      name: `Player ${playerCount}`,
      jerseyNumber: String(playerCount - 1)
    });
    setPlayerTab('info');
    setView('editPlayer');
  };
  
  const handleEditPlayer = (player) => {
    setEditingPlayer({ ...DEFAULT_SCOUTED_PLAYER, ...player });
    setPlayerTab('info');
    setView('editPlayer');
  };
  
  const handleSavePlayer = async () => {
    if (!editingPlayer?.name.trim()) {
      alert('Player name is required');
      return;
    }
    
    const existingIdx = selectedOpponent.players.findIndex(p => p.id === editingPlayer.id);
    const newPlayers = existingIdx >= 0
      ? selectedOpponent.players.map(p => p.id === editingPlayer.id ? editingPlayer : p)
      : [...selectedOpponent.players, editingPlayer];
    
    const updatedOpponent = { ...selectedOpponent, players: newPlayers };
    const newOpponents = opponents.map(o => o.id === updatedOpponent.id ? updatedOpponent : o);
    
    await saveOpponents(newOpponents);
    setSelectedOpponent(updatedOpponent);
    setEditingPlayer(null);
    setView('detail');
  };
  
  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Delete this scouted player?')) return;
    
    const newPlayers = selectedOpponent.players.filter(p => p.id !== playerId);
    const updatedOpponent = { ...selectedOpponent, players: newPlayers };
    const newOpponents = opponents.map(o => o.id === updatedOpponent.id ? updatedOpponent : o);
    
    await saveOpponents(newOpponents);
    setSelectedOpponent(updatedOpponent);
  };
  
  const handleToggleStarter = async (playerId) => {
    const newPlayers = selectedOpponent.players.map(p => 
      p.id === playerId ? { ...p, isStarter: !p.isStarter } : p
    );
    const updatedOpponent = { ...selectedOpponent, players: newPlayers };
    const newOpponents = opponents.map(o => o.id === updatedOpponent.id ? updatedOpponent : o);
    
    await saveOpponents(newOpponents);
    setSelectedOpponent(updatedOpponent);
  };
  
  // Logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload an image'); return; }
    if (file.size > 500 * 1024) { alert('Max 500KB'); return; }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setEditingOpponent(prev => ({ ...prev, logoUrl: event.target.result }));
    };
    reader.readAsDataURL(file);
  };
  
  const applyPreset = (presetName) => {
    const preset = TeamPresets[presetName];
    if (preset && editingOpponent) {
      setEditingOpponent(prev => ({
        ...prev,
        ...preset,
        textColor: preset.numberColor
      }));
    }
  };
  
  // Get team overall rating
  const getTeamOverall = (opponent) => {
    if (opponent.useManualRatings || opponent.players.length < 3) {
      const ratings = opponent.teamRatings || {};
      const values = Object.values(ratings);
      return values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 50;
    } else {
      const calc = calculateTeamRatings(opponent.players);
      if (!calc) return 50;
      const { offense, defense, speed, discipline, depth } = calc;
      return Math.round((offense + defense + speed + discipline + depth) / 5);
    }
  };
  
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-900">
        <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  // ===================== LIST VIEW =====================
  if (view === 'list') {
    return (
      <div className="h-full flex flex-col bg-slate-900">
        <div className="px-6 py-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">🔍 Scouting</h1>
              <p className="text-slate-400 text-sm">{opponents.length} opponent teams</p>
            </div>
            <button onClick={handleAddOpponent}
              className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-500">
              + Add Team
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {opponents.length === 0 ? (
            <div className="text-center py-16 bg-slate-800 rounded-2xl">
              <div className="text-6xl mb-4">🆚</div>
              <h3 className="text-xl font-bold text-white mb-2">No Teams Scouted</h3>
              <p className="text-slate-400 mb-6">Add opponent teams to start scouting</p>
              <button onClick={handleAddOpponent}
                className="px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-500">
                + Scout First Team
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {opponents.map(opponent => {
                const overall = getTeamOverall(opponent);
                return (
                  <div key={opponent.id} 
                    className="bg-slate-800 rounded-xl p-4 hover:bg-slate-750 transition-colors cursor-pointer"
                    onClick={() => handleViewOpponent(opponent)}>
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <JerseyIcon size={70} number={opponent.abbreviation?.slice(0, 2) || '??'}
                          fillColor={opponent.fillColor} outlineColor={opponent.outlineColor}
                          numberColor={opponent.numberColor} numberOutline={opponent.numberOutline}
                          logoUrl={opponent.logoUrl} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-bold text-lg truncate">{opponent.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-400 font-mono">
                            {opponent.abbreviation}
                          </span>
                          {opponent.league && (
                            <span className="text-slate-500 text-xs truncate">{opponent.league}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center gap-1">
                            <span className="text-2xl font-black" style={{ color: getSkillColor(overall) }}>
                              {overall}
                            </span>
                            <span className="text-xs text-slate-500">OVR</span>
                          </div>
                          <span className="text-slate-500 text-sm">
                            {opponent.players?.length || 0} scouted
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // ===================== DETAIL VIEW =====================
  if (view === 'detail' && selectedOpponent) {
    const overall = getTeamOverall(selectedOpponent);
    const calcRatings = calculateTeamRatings(selectedOpponent.players);
    const showCalculated = !selectedOpponent.useManualRatings && selectedOpponent.players.length >= 3;
    const ratings = showCalculated && calcRatings ? calcRatings : selectedOpponent.teamRatings;
    
    const starters = selectedOpponent.players.filter(p => p.isStarter);
    const bench = selectedOpponent.players.filter(p => !p.isStarter);
    
    return (
      <div className="h-full flex flex-col bg-slate-900">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setView('list')} className="p-2 text-white/70 hover:text-white hover:bg-white/20 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="relative">
                {selectedOpponent.logoUrl ? (
                  <img src={selectedOpponent.logoUrl} alt="" className="w-16 h-16 object-contain bg-white rounded-xl p-1" />
                ) : (
                  <JerseyIcon size={64} number={selectedOpponent.abbreviation?.slice(0, 2) || '??'}
                    fillColor={selectedOpponent.fillColor} outlineColor={selectedOpponent.outlineColor}
                    numberColor={selectedOpponent.numberColor} numberOutline={selectedOpponent.numberOutline} />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-black text-white">{selectedOpponent.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-white/20 rounded text-xs text-white font-mono">
                    {selectedOpponent.abbreviation}
                  </span>
                  {selectedOpponent.league && (
                    <span className="text-red-200 text-sm">{selectedOpponent.league}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="text-4xl font-black text-white">{overall}</div>
                <div className="text-xs text-red-200">OVERALL</div>
              </div>
              <button onClick={() => handleEditOpponent(selectedOpponent)}
                className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30">
                ✏️ Edit
              </button>
              <button onClick={() => handleDeleteOpponent(selectedOpponent.id)}
                className="p-2 bg-white/10 text-white/70 rounded-lg hover:bg-red-800 hover:text-white">
                🗑️
              </button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-slate-700 px-6">
          {[
            { id: 'overview', label: '📊 Overview' },
            { id: 'players', label: `👥 Players (${selectedOpponent.players.length})` },
            { id: 'report', label: '📋 Scouting Report' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setDetailTab(tab.id)}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                detailTab === tab.id 
                  ? 'border-red-500 text-white' 
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {detailTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Team Ratings */}
              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Team Ratings</h3>
                  {showCalculated && (
                    <span className="px-2 py-1 bg-green-600/20 text-green-400 text-xs rounded">
                      Calculated from {selectedOpponent.players.length} players
                    </span>
                  )}
                </div>
                <div className="space-y-4">
                  {TEAM_RATING_CATEGORIES.map(cat => {
                    const value = ratings[cat.key] || 50;
                    return (
                      <div key={cat.key}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span>{cat.icon}</span>
                            <span className="text-white font-medium">{cat.label}</span>
                          </div>
                          <span className="text-lg font-bold" style={{ color: getSkillColor(value) }}>{value}</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{ width: `${value}%`, backgroundColor: cat.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Team Info</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">League</span>
                      <span className="text-white">{selectedOpponent.league || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">League Level</span>
                      <span className="text-white capitalize">{selectedOpponent.leagueSkillLevel || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Actual Level</span>
                      <span className="text-white capitalize">{selectedOpponent.actualSkillLevel || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Players Scouted</span>
                      <span className="text-white">{selectedOpponent.players.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Starters Identified</span>
                      <span className="text-white">{starters.length}/5</span>
                    </div>
                  </div>
                </div>
                
                {/* Top Threats */}
                {selectedOpponent.players.length > 0 && (
                  <div className="bg-slate-800 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Top Threats</h3>
                    <div className="space-y-2">
                      {[...selectedOpponent.players]
                        .sort((a, b) => getAverageSkill(b.skills) - getAverageSkill(a.skills))
                        .slice(0, 3)
                        .map((player, idx) => (
                          <div key={player.id} className="flex items-center gap-3 p-2 bg-slate-700 rounded-lg">
                            <span className="text-lg font-bold text-slate-500">#{idx + 1}</span>
                            <JerseyIcon size={36} number={player.jerseyNumber || '0'}
                              fillColor={selectedOpponent.fillColor} outlineColor={selectedOpponent.outlineColor}
                              numberColor={selectedOpponent.numberColor} numberOutline={selectedOpponent.numberOutline} />
                            <div className="flex-1">
                              <div className="text-white font-medium">{player.name}</div>
                              <div className="text-slate-400 text-xs">#{player.jerseyNumber} • {player.position || 'N/A'}</div>
                            </div>
                            <div className="text-lg font-bold" style={{ color: getSkillColor(getAverageSkill(player.skills)) }}>
                              {getAverageSkill(player.skills)}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Players Tab */}
          {detailTab === 'players' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-green-600/20 text-green-400 rounded-lg text-sm">
                    {starters.length} Starters
                  </span>
                  <span className="px-3 py-1 bg-slate-700 text-slate-300 rounded-lg text-sm">
                    {bench.length} Bench
                  </span>
                </div>
                <button onClick={handleAddPlayer}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500">
                  + Scout Player
                </button>
              </div>
              
              {selectedOpponent.players.length === 0 ? (
                <div className="text-center py-12 bg-slate-800 rounded-xl">
                  <div className="text-4xl mb-4">🔍</div>
                  <h4 className="text-lg font-semibold text-white mb-2">No Players Scouted</h4>
                  <p className="text-slate-400 mb-6">Add players to build your scouting report</p>
                  <button onClick={handleAddPlayer}
                    className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-500">
                    + Scout First Player
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Starters */}
                  {starters.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-green-400 mb-3">STARTING LINEUP</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {starters.map(player => (
                          <PlayerCard key={player.id} player={player} opponent={selectedOpponent}
                            onEdit={handleEditPlayer} onDelete={handleDeletePlayer} onToggleStarter={handleToggleStarter} />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Bench */}
                  {bench.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-400 mb-3">BENCH</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {bench.map(player => (
                          <PlayerCard key={player.id} player={player} opponent={selectedOpponent}
                            onEdit={handleEditPlayer} onDelete={handleDeletePlayer} onToggleStarter={handleToggleStarter} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Scouting Report Tab */}
          {detailTab === 'report' && (
            <div className="bg-slate-800 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-6">📋 Scouting Report: {selectedOpponent.name}</h3>
              
              {selectedOpponent.players.length < 3 ? (
                <div className="text-center py-8 bg-slate-700 rounded-xl">
                  <div className="text-4xl mb-4">📊</div>
                  <h4 className="text-lg font-semibold text-white mb-2">Need More Data</h4>
                  <p className="text-slate-400">Scout at least 3 players to generate a full report</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Team Summary */}
                  <div className="p-4 bg-slate-700 rounded-xl">
                    <h4 className="font-bold text-white mb-2">Team Summary</h4>
                    <p className="text-slate-300">
                      {selectedOpponent.name} is a {selectedOpponent.actualSkillLevel || 'competitive'} level team 
                      {selectedOpponent.league ? ` playing in ${selectedOpponent.league}` : ''}. 
                      Based on {selectedOpponent.players.length} scouted players, they have an overall rating of {overall}.
                    </p>
                  </div>
                  
                  {/* Strengths */}
                  <div className="p-4 bg-green-900/30 rounded-xl border border-green-700">
                    <h4 className="font-bold text-green-400 mb-3">💪 Strengths</h4>
                    <ul className="space-y-2 text-green-200">
                      {Object.entries(ratings)
                        .filter(([_, v]) => v >= 60)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 3)
                        .map(([key, value]) => {
                          const cat = TEAM_RATING_CATEGORIES.find(c => c.key === key);
                          return (
                            <li key={key} className="flex items-center gap-2">
                              <span>{cat?.icon}</span>
                              <span>{cat?.label}: <strong>{value}</strong></span>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                  
                  {/* Weaknesses */}
                  <div className="p-4 bg-red-900/30 rounded-xl border border-red-700">
                    <h4 className="font-bold text-red-400 mb-3">⚠️ Weaknesses</h4>
                    <ul className="space-y-2 text-red-200">
                      {Object.entries(ratings)
                        .filter(([_, v]) => v < 50)
                        .sort((a, b) => a[1] - b[1])
                        .slice(0, 3)
                        .map(([key, value]) => {
                          const cat = TEAM_RATING_CATEGORIES.find(c => c.key === key);
                          return (
                            <li key={key} className="flex items-center gap-2">
                              <span>{cat?.icon}</span>
                              <span>{cat?.label}: <strong>{value}</strong></span>
                            </li>
                          );
                        })}
                      {Object.entries(ratings).filter(([_, v]) => v < 50).length === 0 && (
                        <li className="text-slate-400">No significant weaknesses identified</li>
                      )}
                    </ul>
                  </div>
                  
                  {/* Key Players */}
                  <div className="p-4 bg-slate-700 rounded-xl">
                    <h4 className="font-bold text-white mb-3">🎯 Key Players to Watch</h4>
                    <div className="space-y-3">
                      {[...selectedOpponent.players]
                        .sort((a, b) => getAverageSkill(b.skills) - getAverageSkill(a.skills))
                        .slice(0, 5)
                        .map((player, idx) => (
                          <div key={player.id} className="flex items-center gap-4 p-3 bg-slate-800 rounded-lg">
                            <JerseyIcon size={48} number={player.jerseyNumber || '0'} view="back"
                              lastName={player.name?.split(' ').pop()?.toUpperCase()}
                              fillColor={selectedOpponent.fillColor} outlineColor={selectedOpponent.outlineColor}
                              numberColor={selectedOpponent.numberColor} numberOutline={selectedOpponent.numberOutline}
                              textColor={selectedOpponent.textColor}
                              numberY={player.numberY} nameY={player.nameY} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium">{player.name}</span>
                                <span className="text-slate-400 text-sm">#{player.jerseyNumber}</span>
                                {player.isStarter && (
                                  <span className="px-1.5 py-0.5 bg-green-600/30 text-green-400 text-xs rounded">Starter</span>
                                )}
                              </div>
                              <div className="text-slate-400 text-sm">{player.position || 'Position N/A'}</div>
                              <StatDisplay skills={player.skills} compact />
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-black" style={{ color: getSkillColor(getAverageSkill(player.skills)) }}>
                                {getAverageSkill(player.skills)}
                              </div>
                              <div className="text-xs text-slate-500">OVR</div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  
                  {/* Notes */}
                  {selectedOpponent.notes && (
                    <div className="p-4 bg-slate-700 rounded-xl">
                      <h4 className="font-bold text-white mb-2">📝 Coach Notes</h4>
                      <p className="text-slate-300 whitespace-pre-wrap">{selectedOpponent.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // ===================== EDIT TEAM VIEW =====================
  if (view === 'editTeam' && editingOpponent) {
    return (
      <div className="h-full flex flex-col bg-slate-900">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => { setView(selectedOpponent ? 'detail' : 'list'); setEditingOpponent(null); }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-white">
                {opponents.find(o => o.id === editingOpponent.id) ? 'Edit Team' : 'Add Team'}
              </h1>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setView(selectedOpponent ? 'detail' : 'list'); setEditingOpponent(null); }}
                className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">
                Cancel
              </button>
              <button onClick={handleSaveOpponent}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-500">
                💾 Save Team
              </button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-slate-700 px-6">
          {[
            { id: 'info', label: '📋 Team Info' },
            { id: 'jersey', label: '👕 Jersey' },
            { id: 'ratings', label: '📊 Ratings' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setEditTab(tab.id)}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                editTab === tab.id ? 'border-red-500 text-white' : 'border-transparent text-slate-400 hover:text-white'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {editTab === 'info' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="bg-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Basic Info</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Team Name *</label>
                    <input type="text" value={editingOpponent.name}
                      onChange={(e) => setEditingOpponent(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Central High Eagles"
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Abbreviation</label>
                    <input type="text" value={editingOpponent.abbreviation} maxLength={4}
                      onChange={(e) => setEditingOpponent(prev => ({ ...prev, abbreviation: e.target.value.toUpperCase() }))}
                      placeholder="CHE"
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white uppercase" />
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">League Info</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">League Name</label>
                    <input type="text" value={editingOpponent.league || ''}
                      onChange={(e) => setEditingOpponent(prev => ({ ...prev, league: e.target.value }))}
                      placeholder="e.g., YMCA Youth League"
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">League Skill Level</label>
                      <select value={editingOpponent.leagueSkillLevel || ''}
                        onChange={(e) => setEditingOpponent(prev => ({ ...prev, leagueSkillLevel: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                        {SKILL_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Actual Skill Level</label>
                      <select value={editingOpponent.actualSkillLevel || ''}
                        onChange={(e) => setEditingOpponent(prev => ({ ...prev, actualSkillLevel: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                        {SKILL_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Notes</h3>
                <textarea value={editingOpponent.notes || ''}
                  onChange={(e) => setEditingOpponent(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="General notes about this team, their playstyle, tendencies..."
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none" />
              </div>
            </div>
          )}
          
          {editTab === 'jersey' && (
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Preview */}
                <div className="bg-slate-800 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Jersey Preview</h3>
                  <div className="flex justify-center py-6 bg-slate-900 rounded-lg">
                    <JerseyIcon size={180} number={editingOpponent.abbreviation?.slice(0, 2) || '00'}
                      fillColor={editingOpponent.fillColor} outlineColor={editingOpponent.outlineColor}
                      numberColor={editingOpponent.numberColor} numberOutline={editingOpponent.numberOutline}
                      textColor={editingOpponent.textColor} logoUrl={editingOpponent.logoUrl} />
                  </div>
                  
                  {/* Logo */}
                  <div className="mt-4">
                    <label className="block text-sm text-slate-400 mb-2">Team Logo</label>
                    {editingOpponent.logoUrl ? (
                      <div className="flex items-center gap-4">
                        <img src={editingOpponent.logoUrl} alt="" className="w-16 h-16 object-contain bg-white rounded-lg p-1" />
                        <div className="flex flex-col gap-2">
                          <button onClick={() => logoInputRef.current?.click()}
                            className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600">
                            Change
                          </button>
                          <button onClick={() => setEditingOpponent(prev => ({ ...prev, logoUrl: null }))}
                            className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg text-sm hover:bg-red-600/30">
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => logoInputRef.current?.click()}
                        className="w-full py-6 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-red-500 hover:text-red-400">
                        📤 Upload Logo
                      </button>
                    )}
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </div>
                </div>
                
                {/* Colors */}
                <div className="space-y-4">
                  <div className="bg-slate-800 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Jersey Colors</h3>
                    <div className="space-y-3">
                      {[
                        { key: 'fillColor', label: 'Body Color' },
                        { key: 'outlineColor', label: 'Outline' },
                        { key: 'numberColor', label: 'Number Fill' },
                        { key: 'numberOutline', label: 'Number Stroke' }
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center gap-3">
                          <input type="color" value={editingOpponent[key] || '#000000'}
                            onChange={(e) => setEditingOpponent(prev => ({ ...prev, [key]: e.target.value }))}
                            className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-600" />
                          <div className="flex-1">
                            <label className="block text-sm text-white">{label}</label>
                            <input type="text" value={editingOpponent[key] || ''}
                              onChange={(e) => setEditingOpponent(prev => ({ ...prev, [key]: e.target.value }))}
                              className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-slate-300 font-mono text-xs" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-slate-800 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Quick Presets</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(TeamPresets).map(([name, colors]) => (
                        <button key={name} onClick={() => applyPreset(name)}
                          className="flex items-center gap-2 px-3 py-2 bg-slate-700 rounded-lg hover:bg-slate-600 text-xs">
                          <div className="w-5 h-5 rounded" style={{ backgroundColor: colors.fillColor }} />
                          <span className="text-white">{name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {editTab === 'ratings' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Team Ratings</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">
                      {editingOpponent.useManualRatings ? 'Manual' : 'Auto-calculated'}
                    </span>
                    <button
                      onClick={() => setEditingOpponent(prev => ({ ...prev, useManualRatings: !prev.useManualRatings }))}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        editingOpponent.useManualRatings ? 'bg-red-600' : 'bg-green-600'
                      }`}>
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        editingOpponent.useManualRatings ? 'left-1' : 'left-7'
                      }`} />
                    </button>
                  </div>
                </div>
                
                {editingOpponent.useManualRatings ? (
                  <div className="space-y-6">
                    {TEAM_RATING_CATEGORIES.map(cat => (
                      <div key={cat.key}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{cat.icon}</span>
                            <span className="text-white font-medium">{cat.label}</span>
                          </div>
                          <span className="text-lg font-bold" style={{ color: getSkillColor(editingOpponent.teamRatings?.[cat.key] || 50) }}>
                            {editingOpponent.teamRatings?.[cat.key] || 50}
                          </span>
                        </div>
                        <input type="range" min="0" max="100"
                          value={editingOpponent.teamRatings?.[cat.key] || 50}
                          onChange={(e) => setEditingOpponent(prev => ({
                            ...prev,
                            teamRatings: { ...prev.teamRatings, [cat.key]: parseInt(e.target.value) }
                          }))}
                          className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, ${cat.color} 0%, ${cat.color} ${editingOpponent.teamRatings?.[cat.key] || 50}%, #334155 ${editingOpponent.teamRatings?.[cat.key] || 50}%, #334155 100%)`
                          }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-slate-700 rounded-xl">
                    <div className="text-4xl mb-4">🤖</div>
                    <h4 className="text-lg font-semibold text-white mb-2">Auto-Calculated Ratings</h4>
                    <p className="text-slate-400 mb-4">
                      {editingOpponent.players?.length >= 3
                        ? `Ratings calculated from ${editingOpponent.players.length} scouted players`
                        : 'Scout at least 3 players to auto-calculate team ratings'}
                    </p>
                    {editingOpponent.players?.length >= 3 && (
                      <div className="inline-block bg-green-600/20 text-green-400 px-4 py-2 rounded-lg">
                        ✓ Auto-calculation enabled
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // ===================== EDIT PLAYER VIEW =====================
  if (view === 'editPlayer' && editingPlayer && selectedOpponent) {
    return (
      <div className="h-full flex flex-col bg-slate-900">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => { setView('detail'); setEditingPlayer(null); }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-white">
                {selectedOpponent.players.find(p => p.id === editingPlayer.id) ? 'Edit Player' : 'Scout Player'}
              </h1>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setView('detail'); setEditingPlayer(null); }}
                className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">
                Cancel
              </button>
              <button onClick={handleSavePlayer}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-500">
                💾 Save Player
              </button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-slate-700 px-6">
          {[
            { id: 'info', label: '📋 Info' },
            { id: 'skills', label: '📊 Skills' },
            { id: 'jersey', label: '👕 Jersey' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setPlayerTab(tab.id)}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                playerTab === tab.id ? 'border-green-500 text-white' : 'border-transparent text-slate-400 hover:text-white'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {playerTab === 'info' && (
            <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Preview */}
              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex justify-center mb-6">
                  <JerseyIcon size={140} number={editingPlayer.jerseyNumber || '0'} view="back"
                    lastName={editingPlayer.name?.split(' ').pop()?.toUpperCase() || 'PLAYER'}
                    fillColor={selectedOpponent.fillColor} outlineColor={selectedOpponent.outlineColor}
                    numberColor={selectedOpponent.numberColor} numberOutline={selectedOpponent.numberOutline}
                    textColor={selectedOpponent.textColor}
                    numberY={editingPlayer.numberY} nameY={editingPlayer.nameY}
                    numberSize={editingPlayer.numberSize} nameSize={editingPlayer.nameSize} />
                </div>
                
                {/* Quick stats */}
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-black" style={{ color: getSkillColor(getAverageSkill(editingPlayer.skills)) }}>
                      {getAverageSkill(editingPlayer.skills)}
                    </div>
                    <div className="text-xs text-slate-500">OVERALL</div>
                  </div>
                </div>
              </div>
              
              {/* Form */}
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Player Info</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Name *</label>
                      <input type="text" value={editingPlayer.name}
                        onChange={(e) => setEditingPlayer(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Player name"
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Jersey #</label>
                        <input type="text" value={editingPlayer.jerseyNumber} maxLength={2}
                          onChange={(e) => setEditingPlayer(prev => ({ ...prev, jerseyNumber: e.target.value.replace(/\D/g, '') }))}
                          placeholder="0"
                          className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-center" />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Position</label>
                        <select value={editingPlayer.position}
                          onChange={(e) => setEditingPlayer(prev => ({ ...prev, position: e.target.value }))}
                          className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                          <option value="">-</option>
                          <option value="PG">PG</option>
                          <option value="SG">SG</option>
                          <option value="SF">SF</option>
                          <option value="PF">PF</option>
                          <option value="C">C</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Gender</label>
                        <div className="flex gap-1 h-[50px]">
                          <button type="button"
                            onClick={() => setEditingPlayer(prev => ({ ...prev, gender: 'M' }))}
                            className={`flex-1 rounded-lg font-bold text-lg ${
                              editingPlayer.gender === 'M' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'
                            }`}>♂</button>
                          <button type="button"
                            onClick={() => setEditingPlayer(prev => ({ ...prev, gender: 'F' }))}
                            className={`flex-1 rounded-lg font-bold text-lg ${
                              editingPlayer.gender === 'F' ? 'bg-pink-600 text-white' : 'bg-slate-700 text-slate-400'
                            }`}>♀</button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Starter?</label>
                        <button type="button"
                          onClick={() => setEditingPlayer(prev => ({ ...prev, isStarter: !prev.isStarter }))}
                          className={`w-full px-4 py-3 rounded-lg font-medium ${
                            editingPlayer.isStarter ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-400'
                          }`}>
                          {editingPlayer.isStarter ? '✓ Starter' : 'Bench'}
                        </button>
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Threat Level</label>
                        <select value={editingPlayer.threatLevel || 'medium'}
                          onChange={(e) => setEditingPlayer(prev => ({ ...prev, threatLevel: e.target.value }))}
                          className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="elite">Elite</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Scouting Notes</label>
                      <textarea value={editingPlayer.notes || ''}
                        onChange={(e) => setEditingPlayer(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Tendencies, weaknesses, things to watch..."
                        rows={3}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {playerTab === 'skills' && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-white">Skill Ratings</h3>
                  <span className="text-2xl font-black" style={{ color: getSkillColor(getAverageSkill(editingPlayer.skills)) }}>
                    {getAverageSkill(editingPlayer.skills)} OVR
                  </span>
                </div>
                <div className="space-y-6">
                  {SKILL_CATEGORIES.map(skill => (
                    <div key={skill.key}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{skill.icon}</span>
                          <span className="text-white font-medium">{skill.label}</span>
                        </div>
                        <span className="text-lg font-bold" style={{ color: getSkillColor(editingPlayer.skills?.[skill.key] || 50) }}>
                          {editingPlayer.skills?.[skill.key] || 50}
                        </span>
                      </div>
                      <input type="range" min="0" max="100"
                        value={editingPlayer.skills?.[skill.key] || 50}
                        onChange={(e) => setEditingPlayer(prev => ({
                          ...prev,
                          skills: { ...prev.skills, [skill.key]: parseInt(e.target.value) }
                        }))}
                        className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, ${skill.color} 0%, ${skill.color} ${editingPlayer.skills?.[skill.key] || 50}%, #334155 ${editingPlayer.skills?.[skill.key] || 50}%, #334155 100%)`
                        }} />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>Poor</span>
                        <span>Average</span>
                        <span>Elite</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {playerTab === 'jersey' && (
            <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Jersey Preview</h3>
                <div className="flex justify-center py-6 bg-slate-900 rounded-lg">
                  <JerseyIcon size={180} number={editingPlayer.jerseyNumber || '0'} view="back"
                    lastName={editingPlayer.name?.split(' ').pop()?.toUpperCase() || 'PLAYER'}
                    fillColor={selectedOpponent.fillColor} outlineColor={selectedOpponent.outlineColor}
                    numberColor={selectedOpponent.numberColor} numberOutline={selectedOpponent.numberOutline}
                    textColor={selectedOpponent.textColor}
                    numberY={editingPlayer.numberY} nameY={editingPlayer.nameY}
                    numberSize={editingPlayer.numberSize} nameSize={editingPlayer.nameSize} />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">Position & Size</h3>
                    <button onClick={() => setEditingPlayer(prev => ({
                      ...prev,
                      nameY: DEFAULT_POSITIONS.nameY,
                      nameSize: DEFAULT_POSITIONS.nameSize,
                      numberY: DEFAULT_POSITIONS.numberY,
                      numberSize: DEFAULT_POSITIONS.numberSize
                    }))} className="px-3 py-1 text-xs bg-slate-700 text-slate-400 rounded hover:bg-slate-600">Reset</button>
                  </div>
                  
                  {[
                    { key: 'nameY', label: 'Name Vertical', min: 10, max: 50 },
                    { key: 'nameSize', label: 'Name Size', min: 50, max: 150 },
                    { key: 'numberY', label: 'Number Vertical', min: 30, max: 80 },
                    { key: 'numberSize', label: 'Number Size', min: 50, max: 150 }
                  ].map(({ key, label, min, max }) => (
                    <div key={key} className="mb-4">
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-white">{label}</label>
                        <span className="text-sm text-green-400 font-mono">{editingPlayer[key] || 50}%</span>
                      </div>
                      <input type="range" min={min} max={max}
                        value={editingPlayer[key] || 50}
                        onChange={(e) => setEditingPlayer(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-green-500" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return null;
};

// Player Card Component
const PlayerCard = ({ player, opponent, onEdit, onDelete, onToggleStarter }) => {
  const threatColors = getThreatColor(player.threatLevel);
  
  return (
    <div className="bg-slate-800 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="relative">
          <JerseyIcon size={56} number={player.jerseyNumber || '0'} view="back"
            lastName={player.name?.split(' ').pop()?.toUpperCase()}
            fillColor={opponent.fillColor} outlineColor={opponent.outlineColor}
            numberColor={opponent.numberColor} numberOutline={opponent.numberOutline}
            textColor={opponent.textColor}
            numberY={player.numberY} nameY={player.nameY}
            numberSize={player.numberSize} nameSize={player.nameSize} />
          {player.gender && (
            <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              player.gender === 'M' ? 'bg-blue-600 text-white' : 'bg-pink-600 text-white'
            }`}>
              {player.gender === 'M' ? '♂' : '♀'}
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-white font-semibold truncate">{player.name}</h4>
            <span className="text-slate-400 text-sm">#{player.jerseyNumber}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            {player.position && (
              <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-300">{player.position}</span>
            )}
            <span className={`px-2 py-0.5 rounded text-xs ${threatColors.bg} ${threatColors.text}`}>
              {player.threatLevel?.toUpperCase() || 'MEDIUM'}
            </span>
          </div>
          <StatDisplay skills={player.skills} compact />
        </div>
        
        <div className="flex flex-col items-end gap-2">
          <div className="text-2xl font-black" style={{ color: getSkillColor(getAverageSkill(player.skills)) }}>
            {getAverageSkill(player.skills)}
          </div>
          <div className="flex gap-1">
            <button onClick={() => onToggleStarter(player.id)}
              className={`p-1.5 rounded ${player.isStarter ? 'bg-green-600/30 text-green-400' : 'bg-slate-700 text-slate-400'}`}
              title={player.isStarter ? 'Remove from starters' : 'Add to starters'}>
              ⭐
            </button>
            <button onClick={() => onEdit(player)} className="p-1.5 bg-slate-700 text-slate-300 rounded hover:bg-slate-600">✏️</button>
            <button onClick={() => onDelete(player.id)} className="p-1.5 bg-red-600/20 text-red-400 rounded hover:bg-red-600/30">🗑️</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scouting;
